/**
 * No Organization Guard
 *
 * Displays a message and prompts when user doesn't belong to an organization.
 * Shows pending invites if any, otherwise prompts to create an organization.
 */

import { useEffect, useRef, useState } from "react";
import { Building2, LogOut, Mail, Plus } from "lucide-react";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useOrganizationContext } from "@/providers/OrganizationProvider";
import { CreateOrganizationModal } from "./CreateOrganizationModal";
import { PendingInvitesList } from "./PendingInvitesList";
import { Loading } from "@/components/ui/loading";

interface NoOrganizationGuardProps {
  children: React.ReactNode;
}

export function NoOrganizationGuard({ children }: NoOrganizationGuardProps) {
  const { hasOrganization, isLoading, isAuthError, pendingInvites, user, refetchUser, refetchOrganization } =
    useOrganizationContext();
  const { logout } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const authErrorHandled = useRef(false);

  useEffect(() => {
    if (isAuthError && !authErrorHandled.current) {
      authErrorHandled.current = true;
      logout({ logoutParams: { returnTo: window.location.origin + "/login" } });
    }
  }, [isAuthError, logout]);

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin + "/login" } });
  };

  const handleOrgCreated = () => {
    refetchUser();
    refetchOrganization();
  };

  if (isLoading) {
    return <Loading />;
  }

  if (isAuthError) {
    return <Loading />;
  }

  if (hasOrganization) {
    return <>{children}</>;
  }

  const hasPendingInvites = pendingInvites.length > 0;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Welcome to Vortal</CardTitle>
          <CardDescription>
            You need to belong to an organization to use the platform.
            {hasPendingInvites
              ? " You have pending invitations below."
              : " Create a new organization or wait for an invitation."}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {hasPendingInvites && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Mail className="h-4 w-4" />
                Pending Invitations
              </div>
              <PendingInvitesList />
            </div>
          )}

          <div className="relative">
            {hasPendingInvites && (
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
            )}
            {hasPendingInvites && (
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or</span>
              </div>
            )}
          </div>

          <Button
            onClick={() => setShowCreateModal(true)}
            className="w-full"
            size="lg"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create New Organization
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            By creating an organization, you'll become the owner and can invite team members.
          </p>

          {/* User info and logout */}
          <div className="flex items-center justify-between border-t pt-4">
            <span className="text-xs text-muted-foreground">
              Logged in as <span className="font-medium">{user?.name || user?.email}</span>
            </span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="h-auto py-1 px-2">
              <LogOut className="mr-1 h-3 w-3" />
              Log out
            </Button>
          </div>
        </CardContent>
      </Card>

      <CreateOrganizationModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSuccess={handleOrgCreated}
      />
    </div>
  );
}
